import Box from "@mui/material/Box";
import { IconCircuitGround } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import i18nStore from "../../../../i18n/store";
import {
    GridActionsCellItem,
    GridColDef, GridRenderCellParams,
    GridRowId,
    GridRowsProp
} from "@mui/x-data-grid";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ControlPointDuplicateOutlinedIcon from '@mui/icons-material/ControlPointDuplicateOutlined';
import Tooltip from '@mui/material/Tooltip';
import { CreateOrUpdateNodeInterface, NodeInterface } from "../../schemas/nodes";
import Typography from "@mui/material/Typography";
import CustomEditNumberComponent from "../cellRenderers/CustomEditNumberComponent";
import CustomRenderCell from "../cellRenderers/CustomRenderCell";
import GroundingField from "../cellRenderers/GroundingField";
import IsActiveField from "../cellRenderers/IsActiveField";

export const useMuiNodeColDefs = (
    rows: GridRowsProp,
    setRows: (rows: GridRowsProp) => void,
    handlerDeleteClick: (id: GridRowId) => void,
    handlerDuplicateClick: (row: any) => void,
    updateApiFunc: (projectId: string | number, node: CreateOrUpdateNodeInterface) => Promise<NodeInterface>,
): GridColDef[] => {
    const translations = i18nStore.getTranslations;
    const [colDefs, setColDefs] = useState<GridColDef[]>([]);

    const handleDeleteClick = (id: GridRowId) => () => {
        handlerDeleteClick(id);
    };

    const handleDuplicateClick = (id: GridRowId)=> () => {
        const duplicatedRow = rows.find((row) => row.id === id);
        handlerDuplicateClick(duplicatedRow);
    };

    useEffect(() => {
        const newDefColsEquipment: GridColDef[] = [
            {
                field: "id",
                type: 'number',
            },
            {
                field: "is_basis",
                type: 'boolean',
                headerName: translations._tablesNodes.externalGrid,
                editable: false,
                filter: true,
                widthPart: 0.8,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    return <IsActiveField {...params} rows={rows} setRows={setRows} updateApiFunc={updateApiFunc} isActive0 />
                },
            },
            {
                field: "name",                
                renderHeader: () => (
                    <Tooltip title={translations.nameLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations.name}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                widthPart: 3,
                headerAlign: 'center',
            },
            {
                field: "u_nom",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesNodes.voltageClass} placement="top">
                        <Typography variant="subtitle2">
                            V<sub>{translations._tablesNodes.nom}</sub>, {translations._measUnit.kV}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderEditCell: (params) => (
                    <CustomEditNumberComponent {...params} ge={0} />
                ),
            },
            {
                field: "p_load",
                type: 'number',
                headerName: translations.activePowerLoad,
                renderHeader: () => (
                <Tooltip title={translations._tablesNodes.activePowerLoad} placement="top">
                    <Typography variant="subtitle2">
                        P<sub>{translations._tablesNodes.load}</sub>, {translations._measUnit.MW}
                    </Typography>
                </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.row.p_load || 0;
                    return <CustomRenderCell {...params} value={value} realValue={params.row.p_load} />;
                },
                renderEditCell: (params) => (
                    <CustomEditNumberComponent {...params} ge={0} />
                ),
            },
            {
                field: "q_load",
                type: 'number',
                headerName: translations.qLoad,
                renderHeader: () => (
                <Tooltip title={translations._tablesNodes.reactivePowerLoad} placement="top">
                    <Typography variant="subtitle2">
                        Q<sub>{translations._tablesNodes.load}</sub>, {translations._measUnit.Mvar}
                    </Typography>
                </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.row.q_load || 0;
                    return <CustomRenderCell {...params} value={value} realValue={params.row.q_load} />;
                },
                renderEditCell: (params) => (
                    <CustomEditNumberComponent {...params} />
                ),
            },
            {
                field: "grounding",
                type: 'string',
                renderHeader: () => (
                    <Tooltip title={translations.grounding} placement="top">
                        <Box sx={{ fontSize: "1.5rem" }}>
                            <IconCircuitGround />
                        </Box>

                    </Tooltip>
                ),
                renderCell: (params: GridRenderCellParams<any, Date>) => {
                    return <GroundingField {...params} rows={rows} setRows={setRows} updateApiFunc={updateApiFunc} />
                },
                widthPart: 0.5,
                editable: false,
                headerAlign: 'center',
            },
            {
                field: "actions",
                type: 'actions',
                headerName: "",
                sort: false,
                widthPart: 0.5,
                headerAlign: 'center',
                cellClassName: 'actions',
                getActions: ({ id }): any[] => {
                    return [
                        <GridActionsCellItem
                            icon={
                                <Tooltip title={translations.duplicate} placement="top">
                                    <ControlPointDuplicateOutlinedIcon />
                                </Tooltip>
                            }
                            label={translations.duplicate}
                            className="textPrimary"
                            onClick={handleDuplicateClick(id)}
                            color="inherit"
                        />,
                        <GridActionsCellItem
                            icon={
                                <Tooltip title={translations.delete} placement="top">
                                    <DeleteIcon />
                                </Tooltip>
                            }
                            label={translations.delete}
                            onClick={handleDeleteClick(id)}
                            color="inherit"
                        />,
                    ];
                },
            },
        ] as GridColDef[];

        setColDefs(newDefColsEquipment);
    }, [i18nStore.getCurrentLanguageCode, rows]);

    return colDefs;
}
